import React from "react";
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
  Paper, InputAdornment, CircularProgress
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import "./style.css";

const BPUserDetailsComponent = ({
  BPList,
  corporateName,
  selectedRows,
  handleSelectAll,
  handleCheckboxChange,
  editOpen,
  setEditOpen,
  editedName,
  setEditedName,
  handleEditClick,
  handleSaveEdit,
  unlinkOpen,
  setUnlinkOpen,
  handleUnlinkClick,
  handleConfirmUnlink,
  searchValue,
  handleSearchChange,
  handleStatusToggle,
  currentStatusFilter,
  handleAddNewUser,
  handleBulkActivate,
  handleBulkDeactivate,
  handleScroll,
  loading,
  setEditedDepartmentName,
  editedDepartmentName,
  totalCount
}) => {
  const navigate = useNavigate();

  return (
    <Box className="bp-user-management-container">
      <Sidebar />
      <Box className="bp-user-management-content">
        <div className="warehouse-assortment-header">
          <button className="warehouse-assortment-back-btn" onClick={() => navigate(-1)}>
            ←
          </button>
          <h2>BP User Details - {corporateName || ''}</h2>
        </div>

        <Box className="bp-user-controls-container">
          <Box className="bp-user-filter-section">
            <Box className="bp-user-status-toggle-group">
              <Button
                className={`bp-user-status-toggle ${currentStatusFilter === 'active' ? 'active' : ''}`}
                variant={currentStatusFilter === 'active' ? 'contained' : 'outlined'}
                onClick={() => handleStatusToggle('active')}
                sx={{
                  fontWeight: currentStatusFilter === 'active' ? 'bold' : 'normal',
                  backgroundColor: currentStatusFilter === 'active' ? '#1976d2' : 'inherit',
                  color: currentStatusFilter === 'active' ? '#fff' : 'inherit',
                  '&:hover': {
                    backgroundColor: currentStatusFilter === 'active' ? '#1565c0' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                Active ({BPList.filter(u => u.status === true).length})
              </Button>
              <Button
                className={`bp-user-status-toggle ${currentStatusFilter === 'inactive' ? 'inactive' : ''}`}
                variant={currentStatusFilter === 'inactive' ? 'contained' : 'outlined'}
                onClick={() => handleStatusToggle('inactive')}
                sx={{
                  fontWeight: currentStatusFilter === 'inactive' ? 'bold' : 'normal',
                  backgroundColor: currentStatusFilter === 'inactive' ? '#d32f2f' : 'inherit',
                  color: currentStatusFilter === 'inactive' ? '#fff' : 'inherit',
                  '&:hover': {
                    backgroundColor: currentStatusFilter === 'inactive' ? '#c62828' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                Inactive ({BPList.filter(u => u.status === false).length})
              </Button>
            </Box>

            <TextField
              className="bp-user-search-input"
              variant="outlined"
              size="small"
              placeholder="Search by Employee ID/Name/Email"
              value={searchValue}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" className="bp-user-search-icon">
                    <SearchIcon />
                  </InputAdornment>
                ),
                className: "bp-user-search-input-field"
              }}
              sx={{ width: '300px' }}
            />
          </Box>

          <Box className="bp-user-actions-section">
            <Button
              className="bp-user-add-button"
              variant="contained"
              color="success"
              onClick={handleAddNewUser}
            >
              + Add New User
            </Button>
          </Box>
        </Box>

        <Box className="bp-user-table-wrapper">
          <TableContainer component={Paper} className="bp-user-table-container">
            <Table className="bp-user-data-table" stickyHeader>
              <TableHead className="bp-user-table-head">
                <TableRow className="bp-user-table-header-row">
                  <TableCell padding="checkbox" sx={{ backgroundColor: '#f5f5f5' }}>
                    <Checkbox
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      indeterminate={selectedRows.length > 0 && selectedRows.length < BPList.length}
                      checked={BPList.length > 0 && selectedRows.length === BPList.length}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Employee ID</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>E-Mail</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Department</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box 
              className="bp-user-table-body-scroll" 
              onScroll={handleScroll}
              sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}
            >
              <Table className="bp-user-data-table">
                <TableBody>
                  {BPList.length > 0 ? (
                    BPList.map((row, index) => (
                      <TableRow key={row.ucmId || index} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(index)}
                            onChange={() => handleCheckboxChange(index)}
                          />
                        </TableCell>
                        <TableCell>{row.usersCorporateId || '-'}</TableCell>
                        <TableCell>{row.userName || '-'}</TableCell>
                        <TableCell>{row.emailId || '-'}</TableCell>
                        <TableCell>{row.businessName || row.departmentName || '-'}</TableCell>
                        <TableCell>
                          <span style={{
                            color: row.status ? '#4caf50' : '#f44336',
                            fontWeight: 'bold'
                          }}>
                            {row.status ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEditClick(index)} color="primary" size="small">
                            <EditIcon />
                          </IconButton>
                          {row.unlinkRequest === true && (
                            <Button
                              onClick={() => handleUnlinkClick(index)}
                              color="error"
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            >
                              Unlink
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        {loading ? (
                          <CircularProgress size={30} />
                        ) : (
                          <Typography color="textSecondary">No users found.</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </Box>

        {selectedRows.length > 0 && (
          <Box className="bp-user-bulk-action-bar" sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            {currentStatusFilter === "active" ? (
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDeactivate}
                disabled={loading}
                sx={{ minWidth: '200px' }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Deactivate ${selectedRows.length > 1 ? "Users" : "User"} (${selectedRows.length})`
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={handleBulkActivate}
                disabled={loading}
                sx={{ minWidth: '200px' }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Activate ${selectedRows.length > 1 ? "Users" : "User"} (${selectedRows.length})`
                )}
              </Button>
            )}
          </Box>
        )}

        {/* Edit Dialog */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Edit User Details</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              placeholder="Enter Name"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              margin="dense"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Department Name"
              placeholder="Enter Department Name"
              value={editedDepartmentName}
              onChange={(e) => setEditedDepartmentName(e.target.value)}
              margin="dense"
              variant="outlined"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditOpen(false)} variant="outlined" disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              sx={{ backgroundColor: '#00A86B', '&:hover': { backgroundColor: '#008a58' } }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unlink Dialog */}
        <Dialog open={unlinkOpen} onClose={() => setUnlinkOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Unlink</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 15, mt: 1 }}>
              Are you sure you want to unlink this user?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setUnlinkOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUnlink}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Unlink'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default BPUserDetailsComponent;